import * as sentry from '../../src/utils/sentry'
import { DS_setData } from '../DS/DS.Data'
import { IDB_runAsyncMigrations } from './IDB.migrations'
import { IDB_indexedDB } from './IDB.polyfill'
import { IDB_schema } from './IDB.schema'
import { IDB_globalDbSet } from './IDB'
import type { Stub_ILocalStorage } from '../Stub/Stub.localStorage'

// The database name contains an old version number for legacy reasons.
export const IDB_dbName = 'HIVE5.5.0'
export const IDB_dbVersion = 790 // To avoid collisions, always change this comment too: mklchvutyv

export async function IDB_initialise() {
    const { db, oldVersion } = await IDB_open()
    await IDB_runAsyncMigrations(oldVersion)
    return db
}

type indexedDBStub = {
    open(name: string, version?: number): IDBOpenDBRequest
    deleteDatabase(name: string): void
}
export interface IDB_IOpenDBSuccessEvent extends Event {
    target: EventTarget & { result: IDBDatabase }
}
export interface IDB_IOpenDBVersionChangeEvent extends IDBVersionChangeEvent {
    target: EventTarget & {
        onupgradeneeded: (this: IDBOpenDBRequest, ev: IDBVersionChangeEvent) => any
        result: IDBDatabase
        transaction: IDBTransaction
    }
}

export function IDB_open(
    deps: {
        indexedDB: indexedDBStub
        schema: typeof IDB_schema
        sentryErr: typeof sentry.err
        isProduction: boolean
        localStorage: Stub_ILocalStorage
    } = {
        indexedDB: IDB_indexedDB,
        schema: IDB_schema,
        sentryErr: sentry.err,
        isProduction: ENV.isProduction,
        localStorage,
    }
) {
    const request = deps.indexedDB.open(IDB_dbName, IDB_dbVersion)
    let oldVersion = IDB_dbVersion

    return new Promise<{ db: IDBDatabase; oldVersion: number }>((resolve, reject) => {
        request.onerror = () => {
            const opts = { extra: { context: 'Failed to open indexedDB', error: request.error } }
            const errorMessage = request.error ? request.error.message : 'Failed to open indexedDB'
            deps.sentryErr(errorMessage, opts)
            reject(errorMessage)
        }

        request.onsuccess = (event: IDB_IOpenDBSuccessEvent) => {
            const db = event.target.result
            IDB_globalDbSet(db)
            resolve({ db, oldVersion })
            DS_setData('IDBOpen', () => ({ idbConnectionStatus: 'open' }))
        }

        request.onupgradeneeded = function (event: IDB_IOpenDBVersionChangeEvent) {
            /*  Any migrations which need to happen within the 'onupgradeneeded'
                (eg table creations and deletions) go here.
                All other migrations go in IDB.migrations.
                This allows us to more easily
                    - Serialise the migrations (preventing race conditions and conflicts)
                    - Use async await syntax and our query wrappers
            */

            const db = event.target.result

            if (event.oldVersion !== 0) {
                oldVersion = event.oldVersion
            }

            // Create an objectStore for each table in this database
            deps.schema.forEach(({ name, options }) => {
                if (db.objectStoreNames.contains(name)) return
                db.createObjectStore(name, options)
            })

            // Check if tables have been removed without migration
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < db.objectStoreNames.length; i++) {
                const name = db.objectStoreNames[i]
                if (deps.schema.some((table) => table.name === name)) continue
                if (name === 'image_processing_tasks') continue
                if (name === 'domains') continue
                if (name === 'issue_categories') continue
                if (name === 'backup') continue

                const msg = `HIVE[006]: Table '${name}' unexpectedly removed from schema`

                // In development fail, otherwise report back with sentry
                if (deps.isProduction) {
                    deps.sentryErr(msg, {})
                } else {
                    reject(msg)
                }
            }

            // Delete all legacy databases
            if (oldVersion <= 550) {
                try {
                    deps.indexedDB.deleteDatabase('HIVE5.4.0')
                    deps.indexedDB.deleteDatabase('HIVE5.3.0')
                    deps.indexedDB.deleteDatabase('HIVE5.2.0')
                    deps.indexedDB.deleteDatabase('HIVE5.1.0')
                    deps.indexedDB.deleteDatabase('HIVE5.0.1')
                    deps.indexedDB.deleteDatabase('HIVE4.3.0')
                    deps.indexedDB.deleteDatabase('HIVE4.2.1')
                    deps.indexedDB.deleteDatabase('HIVE2.5.8')
                } catch (_) {
                    deps.sentryErr('Failed to remove legacy databases', {})
                }
            }

            // Delete legacy tables
            if (oldVersion < 570) {
                const deleteObjectStore = (objectStoreName: string) => {
                    try {
                        db.deleteObjectStore(objectStoreName)
                    } catch (_) {
                        // Do nothing as tables shouldn't really exist
                    }
                }

                deleteObjectStore('product_categories')
                deleteObjectStore('comments')
                deleteObjectStore('products')
                deleteObjectStore('dropplets')
                deleteObjectStore('sku_lists')
                deleteObjectStore('action_steps')
            }

            // Remove image_processing_tasks
            if (oldVersion < 580) {
                try {
                    db.deleteObjectStore('image_processing_tasks')
                } catch (_) {
                    deps.sentryErr('Failed to remove image_processing_tasks table', {})
                }
            }

            if (oldVersion < 610) {
                try {
                    db.deleteObjectStore('domains')
                } catch (_) {
                    deps.sentryErr('Failed to remove domains table', {})
                }
            }

            if (oldVersion < 710) {
                try {
                    db.deleteObjectStore('issue_categories')
                } catch (_) {
                    deps.sentryErr('Failed to remove issue_categories table', {})
                }
            }
        }
    })
}
